import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { ABOUT_IMAGE } from "@/lib/site";

const PILLARS = [
  "Practical education",
  "Creative thinking",
  "Skill development",
  "Portfolio building",
  "Career preparation",
];

export default function About() {
  return (
    <section id="about" className="relative py-20 md:py-28" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal y={36} className="order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-amber-500/15 via-transparent to-transparent blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img
                src={ABOUT_IMAGE}
                alt="Student learning digital art on a pen tablet at Motion Mind Institute"
                className="h-80 sm:h-[480px] w-full object-cover transition-transform duration-700 hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
              {/* Corner brackets */}
              <span className="absolute left-4 top-4 h-8 w-8 border-l-2 border-t-2 border-brand" />
              <span className="absolute right-4 bottom-4 h-8 w-8 border-r-2 border-b-2 border-brand" />
            </div>
            <motion.div
              className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-amber-500 px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-obsidian shadow-glow-amber"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              data-testid="about-badge"
            >
              Learn · Create · Grow
            </motion.div>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow>About Us</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
              About <span className="text-brand">Motion Mind Institute</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base text-gray-400 leading-relaxed">
              Motion Mind Institute is built to help aspiring creators develop practical digital skills in
              Graphic Design, Photoshop and Video Editing.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-4 text-base text-gray-400 leading-relaxed">
              We believe creative skills are best learned through practice. That's why our approach focuses on
              understanding the tools, applying the concepts and creating meaningful projects.
            </p>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p} delay={0.28 + i * 0.06}>
                <div className="flex items-center gap-3 rounded-2xl border border-white/[0.07] bg-obsidian-card/70 px-4 py-3.5" data-testid={`about-pillar-${i}`}>
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-500">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm text-gray-200">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
